import { Stop } from "@api/graphql/generated/generated-types";
import { StopOrderHint, getDesiredStopIds } from "./stopOrderRules";

interface PlaceOrderStopsInput {
  route: Stop[];
  orderShipmentId: string;
  desiredStopIds: string[];
  newStopIds: string[];
  selectedDate: string;
  isFirstTaskStarted: boolean;
}

function isOnSelectedDate(stop: Stop, selectedDate: string): boolean {
  return stop.effectiveStopDate === selectedDate;
}

/**
 * Puts one order's pending stops in the dispatcher's order inside the driver's route for the
 * selected date. Other orders keep their places: this order's stops swap among the slots they
 * hold, and new stops go in just before the next existing stop. The route screen validates it.
 */
export function placeOrderStopsInDriverRoute({
  route,
  orderShipmentId,
  desiredStopIds,
  newStopIds,
  selectedDate,
  isFirstTaskStarted,
}: PlaceOrderStopsInput): Stop[] {
  const newIdSet = new Set(newStopIds);
  const routeStopById = new Map(route.map((stop) => [stop.id, stop]));
  const startedFirstStopId = isFirstTaskStarted ? route[0]?.id : undefined;
  const desiredForDate = desiredStopIds.filter((id) => {
    const stop = routeStopById.get(id);
    return stop && isOnSelectedDate(stop, selectedDate);
  });
  const existingDesiredIds = desiredForDate.filter(
    (id) => !newIdSet.has(id) && id !== startedFirstStopId,
  );
  const existingDesiredSet = new Set(existingDesiredIds);

  const withoutNewStops = route.filter(
    (stop) => !(newIdSet.has(stop.id) && isOnSelectedDate(stop, selectedDate)),
  );
  const existingStops = existingDesiredIds
    .map((id) => routeStopById.get(id))
    .filter((stop): stop is Stop => !!stop);
  let existingIndex = 0;
  const reordered = withoutNewStops.map((stop) => {
    if (
      stop.shipmentId === orderShipmentId &&
      isOnSelectedDate(stop, selectedDate) &&
      existingDesiredSet.has(stop.id)
    ) {
      const replacement = existingStops[existingIndex];
      existingIndex += 1;
      return replacement;
    }
    return stop;
  });

  let index = 0;
  while (index < desiredForDate.length) {
    if (!newIdSet.has(desiredForDate[index])) {
      index += 1;
      continue;
    }
    const runIds: string[] = [];
    while (index < desiredForDate.length && newIdSet.has(desiredForDate[index])) {
      runIds.push(desiredForDate[index]);
      index += 1;
    }
    const run = runIds
      .map((id) => routeStopById.get(id))
      .filter((stop): stop is Stop => !!stop);
    if (run.length === 0) continue;

    const nextExistingId = desiredForDate.slice(index).find((id) => !newIdSet.has(id));
    const previousExistingId = desiredForDate
      .slice(0, index - runIds.length)
      .reverse()
      .find((id) => !newIdSet.has(id));
    let insertionIndex = nextExistingId
      ? reordered.findIndex((stop) => stop.id === nextExistingId)
      : previousExistingId
        ? reordered.findIndex((stop) => stop.id === previousExistingId) + 1
        : reordered.length;
    if (insertionIndex < 0) insertionIndex = reordered.length;
    if (isFirstTaskStarted && insertionIndex === 0) insertionIndex = 1;
    reordered.splice(insertionIndex, 0, ...run);
  }

  return reordered;
}

interface PlaceStopsInDispatcherOrderProps {
  route: Stop[];
  shipment: { id: string; stops: Parameters<typeof getDesiredStopIds>[1] };
  stopOrderHint: StopOrderHint;
  selectedDate: string;
  isFirstTaskStarted: boolean;
}

/** Puts the order's stops in the dispatcher's order inside the route. The route screen validates the result. */
export function placeStopsInDispatcherOrder({
  route,
  shipment,
  stopOrderHint,
  selectedDate,
  isFirstTaskStarted,
}: PlaceStopsInDispatcherOrderProps): Stop[] {
  const { desiredStopIds, newStopIds } = getDesiredStopIds(stopOrderHint, shipment.stops);
  return placeOrderStopsInDriverRoute({
    route,
    orderShipmentId: shipment.id,
    desiredStopIds,
    newStopIds,
    selectedDate,
    isFirstTaskStarted,
  });
}
